<template>
  <div class="results">
    <div class="result-detail-summary" v-if="result.graphs">
      <div class="overall-result">
        <div class="results-pill">
          Pass: {{ result.pass || 0 }}
        </div>
      </div>
      <div class="overall-result">
        <div class="results-pill results-pill--fail">
          Fail: {{ result.fail || 0 }}
        </div>
      </div>
      <div class="overall-result">
        <div class="results-pill results-pill--rate">
          Pass Rate: {{ getPassRate(result) + '%' }}
        </div>
      </div>
      <div class="result-date">
        <div><strong>Results as of</strong></div><div>{{ result.as_of }}</div>
      </div>
      <button class="button button-secondary button-outline" v-if="result.graphs" 
        @click="download()"
        :disabled="downloading"
      >
        <i class="material-icons" v-if="!downloading">get_app</i><div class="labs-loader" v-if="downloading"></div>
      </button>
    </div>

    <div class="result-nav" v-if="result.graphs">
      <div class="result-nav__inner" ref="inner-nav">
        <div class="result-nav__scroll" ref="scroll-nav">
          <div class="result-link" 
            :class="{'result-link--active': graphIndex === selectedGraphIndex }"
            v-for="(graph, graphIndex) in result.graphs"
            :key="'attr_' + graphIndex"
            @click="setSelectedGraphIndex(graphIndex)"
          >
            {{ attributesDisplayMap[graph.attribute] || graph.attribute }}
          </div>
        </div>
      </div>
    </div>

    <div class="result-detail" :class="{'result-detail--empty': !result.graphs}">
      <div class="result-detail-chart">
        <StackedBarChart :graphIndex="selectedGraphIndex" @download="download" v-if="result.graphs"/>
        <div class="result-detail-chart__empty" v-if="!result.graphs && !calculating">
          <span class="icon-analytics-bars"></span>
          <p>
            The results of your profile will appear here.
          </p>
        </div>
        <div class="labs-loader-container" v-if="calculating">
          <div class="labs-loader"></div>
        </div>
        
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import UtilsService from '@/services/utils.service';
import GAService from '@/services/ga.service';
import StackedBarChart from '@/components/StackedBarChart.vue';
import _ from 'lodash';

@Component({
  components: {
    StackedBarChart
  },
})
export default class ChartCol extends Vue {
  public downloading: boolean = false;
  public showAllAttributes: boolean = false;
  get calculating() {
    return this.$store.state.results.calculating;
  }

  get result() {
    return this.$store.state.results.result || {};
  }

  get profileShowingOnGraph() {
    return this.$store.state.profiles.profileShowingOnGraph || {};
  }

  get selectedGraphIndex() {
    return this.$store.state.results.selectedGraphIndex || 0;
  }

  public get attributesDisplayMap() {
    return this.$store.state.options.attributesDisplayMap || {};
  }

  public setSelectedGraphIndex(index: number) {
    this.$store.commit('setSelectedGraphIndex', index);
    this.$store.commit('triggerClearGraphStickyColumns');
  }

  public getPassRate(res: any) {
    if (_.isEmpty(res)) {
      return '-';
    }

    return UtilsService.round((res.pass / (res.pass + res.fail)) * 100);
  }

  public async download() {
    this.downloading = true;
    try {
      GAService.sendEvent(this.$gtag, 'Download', '', '');
      const res = await this.$store.dispatch('downloadResults');
      UtilsService.exportCSVFile(res, this.profileShowingOnGraph && this.profileShowingOnGraph.name ?  this.profileShowingOnGraph.name : 'Untitled Profile');
    } catch (e) {
      console.log('error handled in store');
    }
    this.downloading = false;
  }
}
</script>
<style scoped lang="scss">
@import '@/scss/partials/_settings.colors.scss';

.results {
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.result-date {
  text-align: right;
  font-size: 12px;
  font-weight: 500;
  padding-left: 10px;
}

.result-detail-summary {
  padding: 0 10px;
  display: flex;
  min-width: 500px;

  .overall-result {
    flex: 1;
    padding: 0 6px;

    .results-pill {
      text-align: center;
      color: #fff;
      font-weight: 600;
      padding: 5px;
      width: 100%;
      background-color: $green;
      border-radius: 20px;
      margin-top: 0;

      &--fail {
        background-color: $red;
      }

      &--rate {
        background-color: #dcdcdc;
        color: #333;
      }
    }
  }

  .button {
    margin-left: 20px;
    height: 34px;
    border-width: 2px;
    i {
      margin: 0;
    }
  }
}

.result-nav {
  border-bottom: 1px solid $border-color;
  width: 100%;
  display: flex;
  padding: 0 10px;
  margin-bottom: 10px;
  margin-top: 10px;

  .result-nav__inner {
    width: 100%;

    .result-nav__scroll {
      display: flex;
      overflow-x: auto;
      align-items: center;

      &::-webkit-scrollbar {
        display: none;
      }

      .result-link {
        padding: 0 10px 10px;
        border-bottom: 4px solid transparent;
        font-weight: 700;
        font-size: 14px;
        white-space: nowrap;
        //overflow-y: auto;

        i {
          font-size: 18px;
          position: relative;
          top: 4px;
        }

        &--active {
          border-bottom-color: $refinitiv-blue;
        }

        &:hover {
          color: $orange;
          cursor: pointer;
        }

        // .tooltip {
        //   margin-top: 100px;
        // }
      }
    }
  }

  
}

.result-detail {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  .result-detail-chart {
    position: relative;
    background-color: #fff;
    border: 1px solid $border-color;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 0;

    .icon-analytics-bars {
      font-size: 48px;
      color: $grey-200;
    }

    p {
      text-align: center;
      margin-top: 20px;
    }

    .result-detail-chart__empty {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  }

  &--empty {
    margin-top: 79px;
  }
}

.result-download-row {
  min-height: 40px;
  margin-top: 25px;
  display: flex;
  justify-content: flex-end;
  .button {
    i {
      color: $refinitiv-blue;
      font-size: 20px;
      position: relative;
      top: 2px;
    }
  }
}

.labs-loader {
  margin-left: 5px;
  width: 24px;
  margin-right: 0;
  height: 24px;

  &::after {
      width: 24px;
      height: 24px;
      top: 6px;
  }
}





</style>